import { ChevronDown, Plus, X } from "lucide-react";
import { useField, useFormikContext } from "formik";
import {
  useState,
  type AnimationEvent,
  type ComponentProps,
  type ReactNode
} from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { AnimatePresence, LayoutGroup, motion } from "@/lib/motion";
import { cn } from "@/lib/utils";

/** The client fields shared by `/clients/new` and the new-case wizard. */
export type ClientDetailsValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  pronouns: string;
  birthday: string;
  email: string;
  phone: string;
};

export const EMPTY_CLIENT_DETAILS: ClientDetailsValues = {
  firstName: "",
  middleName: "",
  lastName: "",
  title: "",
  pronouns: "",
  birthday: "",
  email: "",
  phone: ""
};

type SelectOption = string | { label: string; value: string };

const fieldsetClass = (state: { error?: boolean; focused: boolean }) =>
  cn(
    "relative m-0 w-full min-w-0 rounded-xs border bg-elevated p-0 transition-colors duration-150",
    state.error
      ? "border-danger"
      : state.focused
      ? "border-accent-seed"
      : "border-line-3 hover:border-line-4"
  );

const FloatingFieldLabel = ({
  label,
  isRequired = false
}: {
  label: string;
  isRequired?: boolean;
}) => (
  <>
    {label}
    {isRequired ? <span className="text-danger">{" *"}</span> : null}
  </>
);

/** The notch in the outline that the raised label sits in. */
const FieldNotch = ({
  active,
  children
}: {
  active: boolean;
  children: ReactNode;
}) => (
  <legend
    className={cn(
      "mr-auto ml-2.25 h-0 overflow-hidden p-0 leading-0 whitespace-nowrap",
      active ? "block" : "hidden"
    )}
  >
    <span className="invisible px-1 text-sm">{children}</span>
  </legend>
);

const FloatingLabel = ({
  active,
  children
}: {
  active: boolean;
  children: ReactNode;
}) => (
  <span
    className={cn(
      "pointer-events-none absolute z-1 px-1 leading-4 text-fg-3 transition-all duration-150",
      active
        ? "-top-2.25 left-1.75 text-sm"
        : "top-1/2 left-2.75 -translate-y-1/2 text-md"
    )}
  >
    {children}
  </span>
);

/** The outlined box whose label rises into a notch in the border once active. */
export const FloatingField = ({
  label,
  isRequired = false,
  active,
  focused,
  error = false,
  children
}: {
  label: string;
  isRequired?: boolean;
  active: boolean;
  focused: boolean;
  error?: boolean;
  children: ReactNode;
}) => (
  <fieldset className={fieldsetClass({ error, focused })}>
    <FieldNotch active={active}>
      <FloatingFieldLabel label={label} isRequired={isRequired} />
    </FieldNotch>
    <FloatingLabel active={active}>
      <FloatingFieldLabel label={label} isRequired={isRequired} />
    </FloatingLabel>
    {children}
  </fieldset>
);

type FloatingInputProps = Omit<
  ComponentProps<"input">,
  "value" | "placeholder" | "className"
> & {
  label: string;
  value: string;
  isError?: boolean;
  isRequired?: boolean;
};

/** A text input in a `FloatingField`, not bound to any form library. */
export const FloatingInput = ({
  label,
  value,
  type = "text",
  isError = false,
  isRequired = false,
  onChange,
  onFocus,
  onBlur,
  ...rest
}: FloatingInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isAutofilled, setIsAutofilled] = useState(false);
  const hasValue = value.length > 0;
  const isActive = hasValue || isAutofilled || isFocused;
  const isDateInput = type === "date";
  const hideNativeDatePlaceholder = isDateInput && !hasValue && !isFocused;

  return (
    <FloatingField
      label={label}
      isRequired={isRequired}
      active={isActive}
      focused={isFocused}
      error={isError}
    >
      <input
        {...rest}
        value={value}
        type={type}
        aria-label={label}
        aria-required={isRequired}
        aria-invalid={isError || undefined}
        placeholder=""
        // Chrome fires no event when it autofills; a 1ms animation keyed to
        // :-webkit-autofill surfaces it as `animationstart`.
        onAnimationStart={(e: AnimationEvent) => {
          if (e.animationName === "pulse") setIsAutofilled(true);
        }}
        onChange={(e) => {
          if (isAutofilled) setIsAutofilled(false);
          onChange?.(e);
        }}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
        className={cn(
          "h-10.5 w-full rounded-xs border-0 bg-transparent px-3.25 text-md text-fg outline-none [&:-webkit-autofill]:animate-[pulse_1ms]",
          hasValue && "font-medium",
          hideNativeDatePlaceholder &&
            "text-transparent [&::-webkit-calendar-picker-indicator]:opacity-45 [&::-webkit-datetime-edit]:opacity-0"
        )}
      />
    </FloatingField>
  );
};

export const FloatingFormInput = ({
  name,
  label,
  type = "text",
  isError = false,
  isRequired = false
}: {
  name: string;
  label: string;
  type?: string;
  isError?: boolean;
  isRequired?: boolean;
}) => {
  const [field, meta] = useField(name);
  return (
    <FloatingInput
      name={field.name}
      value={field.value ?? ""}
      onChange={field.onChange}
      onBlur={field.onBlur}
      label={label}
      type={type}
      isRequired={isRequired}
      isError={isError || (meta.touched && Boolean(meta.error))}
    />
  );
};

export const FloatingFormSelect = ({
  name,
  label,
  options
}: {
  name: string;
  label: string;
  options: SelectOption[];
}) => {
  const [field, , helpers] = useField(name);
  const [isOpen, setIsOpen] = useState(false);
  const hasValue = Boolean(field.value);
  const isActive = hasValue || isOpen;

  const normalizedOptions = options.map((option) =>
    typeof option === "string" ? { label: option, value: option } : option
  );
  const selectedOption = normalizedOptions.find(
    (option) => option.value === field.value
  );

  return (
    <FloatingField label={label} active={isActive} focused={isOpen}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-10.5 w-full items-center justify-between bg-transparent px-3.25 text-left outline-hidden"
          >
            <span
              className={cn(
                "truncate text-md",
                hasValue ? "font-medium text-fg" : "text-transparent"
              )}
            >
              {selectedOption?.label ?? label}
            </span>
            <ChevronDown className="size-3.5 text-fg-3" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={8} className="min-w-55">
          {normalizedOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => {
                helpers.setValue(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </FloatingField>
  );
};

export const toBirthdayPayload = (value: string) => {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

type OptionalFieldKey =
  | "middleName"
  | "title"
  | "pronouns"
  | "birthday"
  | "phone";

type OptionalFieldConfig = {
  key: OptionalFieldKey;
  label: string;
  kind: "input" | "select";
  inputType?: string;
  options?: SelectOption[];
};

const OPTIONAL_FIELDS: OptionalFieldConfig[] = [
  { key: "middleName", label: "Middle Name", kind: "input" },
  {
    key: "title",
    label: "Title",
    kind: "select",
    options: ["Mr.", "Ms.", "Dr."]
  },
  {
    key: "pronouns",
    label: "Pronouns",
    kind: "select",
    options: [
      { label: "He/Him/His", value: "he/him" },
      { label: "She/Her/Hers", value: "she/her" },
      { label: "They/Them/Theirs", value: "they/them" }
    ]
  },
  {
    key: "birthday",
    label: "Date of Birth",
    kind: "input",
    inputType: "date"
  },
  { key: "phone", label: "Phone Number", kind: "input", inputType: "tel" }
];

const hasOptionalFieldValue = (
  values: ClientDetailsValues,
  key: OptionalFieldKey
) => {
  const value = values[key];
  return typeof value === "string" && value.trim().length > 0;
};

const OPTIONAL_FIELD_BY_KEY = Object.fromEntries(
  OPTIONAL_FIELDS.map((field) => [field.key, field])
) as Record<OptionalFieldKey, OptionalFieldConfig>;

const OPTIONAL_FIELD_LAYOUT_TRANSITION = {
  duration: 0.42,
  ease: [0.4, 0, 0.2, 1] as const
};

const FormFieldCell = ({
  children,
  showRemoveHover = false,
  reserveRemoveHandleSpace = false
}: {
  children: ReactNode;
  showRemoveHover?: boolean;
  reserveRemoveHandleSpace?: boolean;
}) => (
  <div
    className={cn(
      "relative w-full overflow-visible pt-2.5",
      reserveRemoveHandleSpace && "pr-0.5",
      showRemoveHover && "group/field"
    )}
  >
    {children}
  </div>
);

const AnimatedFieldShell = ({ children }: { children: ReactNode }) => (
  <motion.div
    layout
    transition={{ layout: OPTIONAL_FIELD_LAYOUT_TRANSITION }}
    className="min-w-0 flex-1 self-stretch overflow-visible"
  >
    {children}
  </motion.div>
);

const AnimatedOptionalSlot = ({
  slotKey,
  visible,
  children
}: {
  slotKey: string;
  visible: boolean;
  children: ReactNode;
}) => (
  <AnimatePresence initial={false} mode="popLayout">
    {visible ? (
      <motion.div
        key={slotKey}
        layout
        initial={{ opacity: 0, flex: "0 1 0px" }}
        animate={{ opacity: 1, flex: "1 1 0px" }}
        exit={{ opacity: 0, flex: "0 1 0px" }}
        transition={{
          layout: OPTIONAL_FIELD_LAYOUT_TRANSITION,
          opacity: { duration: 0.22 },
          flex: OPTIONAL_FIELD_LAYOUT_TRANSITION
        }}
        className="min-w-0 flex-1 self-stretch overflow-visible"
      >
        {children}
      </motion.div>
    ) : null}
  </AnimatePresence>
);

const AnimatedFormRow = ({
  rowKey,
  children
}: {
  rowKey: string;
  children: ReactNode;
}) => (
  <motion.div
    key={rowKey}
    layout
    transition={{ layout: OPTIONAL_FIELD_LAYOUT_TRANSITION }}
    className="flex w-full flex-col items-stretch gap-4 overflow-visible md:flex-row md:items-start"
  >
    {children}
  </motion.div>
);

const AnimatedCollapsibleRow = ({
  rowKey,
  visible,
  children
}: {
  rowKey: string;
  visible: boolean;
  children: ReactNode;
}) => (
  <AnimatePresence initial={false} mode="popLayout">
    {visible ? (
      <motion.div
        key={rowKey}
        layout
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{
          layout: OPTIONAL_FIELD_LAYOUT_TRANSITION,
          opacity: { duration: 0.22 },
          height: OPTIONAL_FIELD_LAYOUT_TRANSITION
        }}
        className="w-full overflow-visible"
      >
        <AnimatedFormRow rowKey={`${rowKey}-inner`}>{children}</AnimatedFormRow>
      </motion.div>
    ) : null}
  </AnimatePresence>
);

/** The "Optional" row of buttons that each reveal a hidden field. */
export const OptionalFieldTags = <K extends string>({
  fields,
  onAdd
}: {
  fields: ReadonlyArray<{ key: K; label: string }>;
  onAdd: (key: K) => void;
}) => (
  <div className="flex flex-col items-start gap-3 pt-1">
    <span className="text-sm leading-none text-fg-3">Optional</span>
    <div className="flex flex-wrap items-center gap-3">
      {fields.map((field) => (
        <Button
          key={field.key}
          type="button"
          variant="secondary"
          onClick={() => onAdd(field.key)}
        >
          <Plus />
          {field.label}
        </Button>
      ))}
    </div>
  </div>
);

const OptionalFormField = ({
  fieldKey,
  onRemove
}: {
  fieldKey: OptionalFieldKey;
  onRemove: () => void;
}) => {
  const field = OPTIONAL_FIELD_BY_KEY[fieldKey];
  const [formikField] = useField(fieldKey);
  const isEmpty = !(
    typeof formikField.value === "string" && formikField.value.trim().length > 0
  );

  return (
    <FormFieldCell showRemoveHover reserveRemoveHandleSpace>
      {field.kind === "select" ? (
        <FloatingFormSelect
          name={field.key}
          label={field.label}
          options={field.options ?? []}
        />
      ) : (
        <FloatingFormInput
          name={field.key}
          label={field.label}
          type={field.inputType}
        />
      )}
      {isEmpty ? (
        <button
          type="button"
          aria-label={`Remove ${field.label}`}
          onClick={onRemove}
          className="absolute top-1 -right-1.5 z-5 flex size-5 cursor-pointer items-center justify-center rounded-full border border-line-3 bg-elevated text-fg-3 opacity-0 transition-opacity group-hover/field:opacity-100 hover:bg-fill-1 hover:text-fg"
        >
          <X className="size-3" aria-hidden />
        </button>
      ) : null}
    </FormFieldCell>
  );
};

export const ClientDetailsFields = ({
  emailError,
  className
}: {
  emailError: string | null;
  className?: string;
}) => {
  const { values, setFieldValue } = useFormikContext<ClientDetailsValues>();
  const [revealedFields, setRevealedFields] = useState<Set<OptionalFieldKey>>(
    () => new Set()
  );

  const revealField = (key: OptionalFieldKey) => {
    setRevealedFields((prev) => new Set(prev).add(key));
  };

  const removeField = (key: OptionalFieldKey) => {
    setRevealedFields((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setFieldValue(key, "");
  };

  const isFieldVisible = (key: OptionalFieldKey) =>
    revealedFields.has(key) || hasOptionalFieldValue(values, key);

  const hiddenOptionalFields = OPTIONAL_FIELDS.filter(
    (field) => !isFieldVisible(field.key)
  );

  const showDemographicsRow =
    isFieldVisible("title") ||
    isFieldVisible("pronouns") ||
    isFieldVisible("birthday");

  return (
    <div className={cn("max-w-160 overflow-visible", className)}>
      <LayoutGroup id="client-details-fields">
        <div className="flex flex-col gap-4">
          <AnimatedFormRow rowKey="name-row">
            <AnimatedFieldShell>
              <FormFieldCell>
                <FloatingFormInput
                  name="firstName"
                  label="First Name"
                  isRequired
                />
              </FormFieldCell>
            </AnimatedFieldShell>
            <AnimatedOptionalSlot
              slotKey="middleName"
              visible={isFieldVisible("middleName")}
            >
              <OptionalFormField
                fieldKey="middleName"
                onRemove={() => removeField("middleName")}
              />
            </AnimatedOptionalSlot>
            <AnimatedFieldShell>
              <FormFieldCell>
                <FloatingFormInput
                  name="lastName"
                  label="Last Name"
                  isRequired
                />
              </FormFieldCell>
            </AnimatedFieldShell>
          </AnimatedFormRow>

          <AnimatedCollapsibleRow
            rowKey="demographics-row"
            visible={showDemographicsRow}
          >
            <AnimatedOptionalSlot
              slotKey="title"
              visible={isFieldVisible("title")}
            >
              <OptionalFormField
                fieldKey="title"
                onRemove={() => removeField("title")}
              />
            </AnimatedOptionalSlot>
            <AnimatedOptionalSlot
              slotKey="pronouns"
              visible={isFieldVisible("pronouns")}
            >
              <OptionalFormField
                fieldKey="pronouns"
                onRemove={() => removeField("pronouns")}
              />
            </AnimatedOptionalSlot>
            <AnimatedOptionalSlot
              slotKey="birthday"
              visible={isFieldVisible("birthday")}
            >
              <OptionalFormField
                fieldKey="birthday"
                onRemove={() => removeField("birthday")}
              />
            </AnimatedOptionalSlot>
          </AnimatedCollapsibleRow>

          <AnimatedFormRow rowKey="contact-row">
            <AnimatedFieldShell>
              <FormFieldCell>
                <FloatingFormInput
                  name="email"
                  label="Email Address"
                  type="email"
                  isRequired
                  isError={Boolean(emailError)}
                />
              </FormFieldCell>
            </AnimatedFieldShell>
            <AnimatedOptionalSlot
              slotKey="phone"
              visible={isFieldVisible("phone")}
            >
              <OptionalFormField
                fieldKey="phone"
                onRemove={() => removeField("phone")}
              />
            </AnimatedOptionalSlot>
          </AnimatedFormRow>

          <AnimatePresence initial={false} mode="popLayout">
            {hiddenOptionalFields.length > 0 ? (
              <motion.div
                key="optional-tags-row"
                layout
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ layout: OPTIONAL_FIELD_LAYOUT_TRANSITION }}
                className="pt-1"
              >
                <OptionalFieldTags
                  fields={hiddenOptionalFields}
                  onAdd={revealField}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </LayoutGroup>
    </div>
  );
};
